import React, { useRef, useState } from "react";
import {
  Box, Grid, Typography, Button, Paper, Avatar, Divider,
  Dialog, DialogActions, DialogContent, DialogTitle, TextField, alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import TvIcon from "@mui/icons-material/Tv";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import HistoryIcon from "@mui/icons-material/History";
import StorageIcon from "@mui/icons-material/Storage";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import {
  NAVY, NAVY_DARK, GOLD, GOLD_SOFT, PAGE_BG, BORDER,
  TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../theme/tokens";
import {
  useGetDashboardSummaryQuery,
  useCreatePlaylistMutation,
} from "../../Api/attoDeskApi";
import PageLoader from "../../Components/Loader/PageLoader";
import { useNotifier } from "../../Core/Notifier";
import { useUploads } from "../../Components/Uploads/UploadContext";

/* ──────────────────────────────────────────────────────────────────────── */

interface DashboardData {
  devices: { total: number; online: number; offline: number; alerts_24h: number };
  playlists: { total: number };
  playlist_groups?: { total: number; active: number };
  media: { total: number; recent_24h: number; storage_bytes: number };
  recent_activity: Array<{
    id: number;
    type: string;
    label: string;
    description: string;
    created_at: string | null;
  }>;
}

const ACTIVITY_STYLE: Record<string, { tint: string; bg: string; icon: React.ReactElement }> = {
  device_registered:        { tint: "#3b82f6", bg: "#dbeafe", icon: <TvIcon /> },
  device_created:           { tint: "#3b82f6", bg: "#dbeafe", icon: <TvIcon /> },
  device_online:            { tint: "#10b981", bg: "#d1fae5", icon: <FiberManualRecordIcon /> },
  device_offline:           { tint: "#ef4444", bg: "#fee2e2", icon: <WifiOffIcon /> },
  device_playlist_assigned: { tint: "#7c3aed", bg: "#ede9fe", icon: <PlaylistPlayIcon /> },
  playlist_updated:         { tint: "#f59e0b", bg: "#fef3c7", icon: <PlaylistPlayIcon /> },
  media_uploaded:           { tint: "#00838f", bg: "#cffafe", icon: <PermMediaIcon /> },
};

const formatBytes = (b: number): string => {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
};

const formatRelative = (iso: string | null): string => {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

/* ──────────────────────────────────────────────────────────────────────── */

const MetricCard: React.FC<{
  label: string;
  value: number | string;
  icon: React.ReactElement;
  tint: string;
  bg: string;
  subValue?: string;
  emphasised?: boolean;
}> = ({ label, value, icon, tint, bg, subValue, emphasised }) => (
  <Paper elevation={0} sx={{
    p: 2.5, borderRadius: "16px",
    border: `1px solid ${emphasised ? alpha(GOLD, 0.4) : BORDER}`,
    boxShadow: emphasised
      ? `0 8px 24px ${alpha(GOLD, 0.18)}`
      : "0 2px 6px rgba(11,30,45,0.05)",
    height: "100%",
    bgcolor: "#fff",
    position: "relative",
    overflow: "hidden",
  }}>
    {emphasised && (
      <Box sx={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
      }} />
    )}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
      <Avatar sx={{ bgcolor: bg, color: tint, width: 40, height: 40 }}>
        {React.cloneElement(icon, { sx: { fontSize: 20 } })}
      </Avatar>
      <Typography sx={{
        fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{
      fontSize: 32, fontWeight: 800, color: NAVY, lineHeight: 1.1, letterSpacing: "-0.02em",
    }}>
      {value}
    </Typography>
    {subValue && (
      <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.5 }}>
        {subValue}
      </Typography>
    )}
  </Paper>
);

const QuickActionCard: React.FC<{
  label: string;
  description: string;
  icon: React.ReactElement;
  onClick: () => void;
  disabled?: boolean;
}> = ({ label, description, icon, onClick, disabled }) => (
  <Paper
    elevation={0}
    onClick={disabled ? undefined : onClick}
    sx={{
      p: 2.5, borderRadius: "16px",
      border: `1px solid ${BORDER}`,
      bgcolor: "#fff",
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.6 : 1,
      transition: "all 0.2s ease",
      "&:hover": disabled ? {} : {
        borderColor: alpha(GOLD, 0.6),
        boxShadow: `0 8px 22px ${alpha(GOLD, 0.18)}`,
        transform: "translateY(-2px)",
      },
      display: "flex", alignItems: "center", gap: 2,
    }}
  >
    <Box sx={{
      width: 44, height: 44, borderRadius: 2.5,
      background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
      color: NAVY_DARK,
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: `0 4px 12px ${alpha(GOLD, 0.4)}`,
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 22 } })}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
        {description}
      </Typography>
    </Box>
  </Paper>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{
    fontSize: 11, fontWeight: 700, color: NAVY,
    textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.5,
  }}>
    {children}
  </Typography>
);

/* ──────────────────────────────────────────────────────────────────────── */

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const { data, isLoading, refetch } = useGetDashboardSummaryQuery();
  const [createPlaylist, { isLoading: isCreatingPlaylist }] = useCreatePlaylistMutation();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { enqueue, hasActive } = useUploads();
  const [playlistDialog, setPlaylistDialog] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState("");

  if (isLoading) return <PageLoader height="20vh" />;
  const summary = data?.data as unknown as DashboardData | undefined;

  const handleUploadClick = () => fileInputRef.current?.click();
  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const results = await Promise.allSettled(
      Array.from(files).map(f => enqueue(f))
    );
    const ok = results.filter(r => r.status === "fulfilled").length;
    const failed = results.filter(r => r.status === "rejected");
    if (ok > 0) {
      showMessage(`Uploaded ${ok} file${ok === 1 ? "" : "s"}`);
      refetch();
    }
    failed.forEach((r: any) => {
      const msg = r.reason?.message || "Upload failed";
      if (msg && !msg.toLowerCase().includes("cancel")) showErrorMessage(msg);
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCreatePlaylist = async () => {
    if (!newPlaylistName.trim()) { showErrorMessage("Name is required"); return; }
    try {
      const res = await createPlaylist({
        name: newPlaylistName.trim(), is_active: true,
      }).unwrap();
      if (res.status) {
        const created = (res as any).data;
        showMessage("Playlist created");
        setPlaylistDialog(false); setNewPlaylistName("");
        if (created?.id) navigate(`/playlist/edit/${created.id}`);
      }
    } catch {
      showErrorMessage("Failed to create playlist");
    }
  };

  const activity = summary?.recent_activity ?? [];

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* ── Welcome Header ── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: `1px solid ${BORDER}`, px: 3, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 3,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
            boxShadow: `0 6px 18px ${alpha(NAVY, 0.35)}`,
            border: `1px solid ${alpha(GOLD, 0.35)}`,
          }}>
            <TrendingUpIcon sx={{ fontSize: 22, color: GOLD }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={22} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
              Dashboard
            </Typography>
            <Typography fontSize={12} color={TEXT_SECONDARY}>
              Overview of your digital signage network
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ p: 3 }}>

        {/* Primary metrics row */}
        <SectionLabel>Network Status</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="Total Devices"
              value={summary?.devices.total ?? 0}
              icon={<TvIcon />}
              tint="#3b82f6" bg="#dbeafe"
              subValue={`${summary?.playlists.total ?? 0} playlist${summary?.playlists.total === 1 ? "" : "s"} configured`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="Online Devices"
              value={summary?.devices.online ?? 0}
              icon={<FiberManualRecordIcon />}
              tint="#10b981" bg="#d1fae5"
              subValue="Heartbeat within 5 min"
              emphasised={(summary?.devices.online ?? 0) > 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="Offline Devices"
              value={summary?.devices.offline ?? 0}
              icon={<WifiOffIcon />}
              tint="#ef4444" bg="#fee2e2"
              subValue="No recent heartbeat"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="Playlist Groups"
              value={summary?.playlist_groups?.total ?? 0}
              icon={<CollectionsBookmarkIcon />}
              tint="#7c3aed" bg="#ede9fe"
              subValue={`${summary?.playlist_groups?.active ?? 0} active`}
            />
          </Grid>
        </Grid>

        {/* Secondary metrics row */}
        <SectionLabel>Library &amp; Alerts</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="Total Playlists"
              value={summary?.playlists.total ?? 0}
              icon={<PlaylistPlayIcon />}
              tint="#6366f1" bg="#e0e7ff"
              subValue="Across the catalog"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="Media Files"
              value={summary?.media.total ?? 0}
              icon={<PermMediaIcon />}
              tint="#00838f" bg="#cffafe"
              subValue="Images and videos"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="Recent Uploads"
              value={summary?.media.recent_24h ?? 0}
              icon={<NewReleasesIcon />}
              tint="#f59e0b" bg="#fef3c7"
              subValue="Past 24 hours"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="Device Alerts"
              value={summary?.devices.alerts_24h ?? 0}
              icon={<NotificationsActiveIcon />}
              tint="#ef4444" bg="#fee2e2"
              subValue="Offline events, past 24h"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="Storage Usage"
              value={formatBytes(summary?.media.storage_bytes ?? 0)}
              icon={<StorageIcon />}
              tint="#64748b" bg="#f1f5f9"
              subValue="Total of all media files"
            />
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <SectionLabel>Quick Actions</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <QuickActionCard
              label="Add Device"
              description="Provision a new screen"
              icon={<AddIcon />}
              onClick={() => navigate("/device/create")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <QuickActionCard
              label={hasActive ? "Uploading…" : "Upload Media"}
              description="Images up to 500 MB · MP4 videos"
              icon={<CloudUploadIcon />}
              onClick={handleUploadClick}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <QuickActionCard
              label="Create Playlist"
              description="Build a media sequence"
              icon={<PlaylistAddIcon />}
              onClick={() => setPlaylistDialog(true)}
            />
          </Grid>
        </Grid>

        {/* Recent Activity */}
        <SectionLabel>Recent Activity</SectionLabel>
        <Paper elevation={0} sx={{
          borderRadius: "16px", border: `1px solid ${BORDER}`,
          overflow: "hidden", boxShadow: "0 2px 6px rgba(11,30,45,0.05)",
          bgcolor: "#fff",
        }}>
          {activity.length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center", color: TEXT_SECONDARY }}>
              <HistoryIcon sx={{ fontSize: 40, opacity: 0.5, mb: 1.5 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: NAVY }}>
                No activity yet
              </Typography>
              <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                Register a device, upload media, or build a playlist to see events here.
              </Typography>
            </Box>
          ) : (
            activity.map((a, idx) => {
              const style = ACTIVITY_STYLE[a.type] ?? {
                tint: "#64748b", bg: "#f1f5f9", icon: <HistoryIcon />,
              };
              return (
                <Box key={a.id} sx={{
                  display: "flex", alignItems: "center", gap: 2,
                  px: 2.5, py: 1.75,
                  borderTop: idx === 0 ? "none" : `1px solid ${BORDER}`,
                  transition: "background 0.15s ease",
                  "&:hover": { bgcolor: "#f8fafc" },
                }}>
                  <Avatar sx={{
                    bgcolor: style.bg, color: style.tint, width: 36, height: 36,
                  }}>
                    {React.cloneElement(style.icon, { sx: { fontSize: 18 } })}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: 13.5, fontWeight: 700, color: NAVY, lineHeight: 1.3,
                    }}>
                      {a.label}
                    </Typography>
                    <Typography sx={{
                      fontSize: 12, color: TEXT_SECONDARY,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {a.description}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY, fontWeight: 500 }}>
                    {formatRelative(a.created_at)}
                  </Typography>
                </Box>
              );
            })
          )}
        </Paper>
      </Box>

      <input
        ref={fileInputRef} type="file"
        accept=".jpg,.jpeg,.png,.webp,.mp4"
        multiple hidden
        onChange={(e) => handleFiles(e.target.files)}
      />

      <Dialog
        open={playlistDialog} onClose={() => setPlaylistDialog(false)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: NAVY, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 2,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.12)} 0%, ${alpha("#7c3aed", 0.06)} 100%)`,
            }}>
              <PlaylistAddIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
            </Box>
            Create Playlist
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{
            fontSize: 12, fontWeight: 600, color: "#475569",
            mb: 0.75, textTransform: "uppercase", letterSpacing: "0.02em",
          }}>
            Playlist Name *
          </Typography>
          <TextField
            autoFocus fullWidth size="small"
            placeholder="e.g. Morning Loop"
            value={newPlaylistName}
            onChange={(e) => setNewPlaylistName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleCreatePlaylist(); }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5, fontSize: 13, bgcolor: "#fff",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&.Mui-focused fieldset": { borderColor: "#7c3aed", borderWidth: 2 },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setPlaylistDialog(false)}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#64748b" }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation
            disabled={isCreatingPlaylist} onClick={handleCreatePlaylist}
            sx={{
              borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
              background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
              "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
            }}>
            {isCreatingPlaylist ? "Creating…" : "Create & Edit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
